import type { Destination, DestinationPlan } from './places/destinations';

/** A country's span in month columns, inclusive. */
export type TimelineRow = { dest: Destination; from: number; to: number; dates: string };
/** Month columns (`YYYY-MM`) from the first month of the plan to the last, each country's span, and this month's column. */
export type Timeline = { months: string[]; rows: TimelineRow[]; now: number | null };

const monthOf = (day: string) => day.slice(0, 7);
function addMonth(m: string): string {
  const [y, mo] = m.split('-').map(Number) as [number, number];
  return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, '0')}`;
}

/**
 * The plan month by month. The start port has no arrival, so it runs from this month to its departure;
 * every other country runs from its first arrival to its last departure.
 */
export function buildTimeline(pages: DestinationPlan[], today: string, dates: (p: DestinationPlan) => string = () => ''): Timeline {
  if (!pages.length) return { months: [], rows: [], now: null };
  const start = (p: DestinationPlan) => monthOf(p.from ?? (today < p.to ? today : p.to));
  const first = pages.map(start).reduce((a, b) => (b < a ? b : a));
  const last = pages.map(p => monthOf(p.to)).reduce((a, b) => (b > a ? b : a));
  const months: string[] = [];
  for (let m = first; m <= last; m = addMonth(m)) months.push(m);
  const at = (m: string) => months.indexOf(m);
  const now = at(monthOf(today));
  return {
    months,
    rows: pages.map(p => ({ dest: p.dest, from: at(start(p)), to: at(monthOf(p.to)), dates: dates(p) })),
    now: now >= 0 ? now : null,
  };
}
